import React from 'react';
import BuilderSelectMenu from '../../components/Builder/BuilderSelectMenu';
import { assertNotNil } from '../../utils/types';
import { ReactSelectMenu } from '../../components/Dropdown/StyledReactSelect';
import { MultiValue, MultiValueGenericProps } from 'react-select';
import { BasicDropdownIndicator } from '../../components/Dropdown/StyledReactSelect/SelectUtils';
import { BasicEntity } from '../../components/EntityLabels';
import { IBasicSchema } from './types';
import { useBootstrapSelector } from '../../redux/selectorHooks';
import { theme } from '../../styles/theme';

interface IProps {
  setSchemas: (schemas: IBasicSchema[]) => void;
  selectedSchemas: IBasicSchema[];
  isMulti: boolean;
  schemaOptions: IBasicSchema[];
}

/**
 * PermissionSchemasSelector is a component to render a dropdown menu for selected schemas in permission editable list
 */
const PermissionSchemasSelector = ({
  setSchemas,
  selectedSchemas,
  isMulti,
  schemaOptions,
}: IProps) => {
  const permissionConfig = useBootstrapSelector('permissionConfig');
  assertNotNil(permissionConfig);
  const { allSchema } = permissionConfig;
  const onChange = (newSchemas: MultiValue<IBasicSchema>) => {
    // case 1: if allSchema is already selected, drop all schema item
    if (selectedSchemas.find((schema) => schema.id === allSchema.id)) {
      setSchemas([
        ...newSchemas.filter((schema) => schema.id !== allSchema.id),
      ]);
    } else if (newSchemas.find((schema) => schema.id === allSchema.id)) {
      // case 2: if allSchema is newly selected, drop all old selected schemas
      setSchemas([allSchema]);
    } else if (isMulti) {
      // case 3: no all schema involved, just update the selected schemas if multi / single
      setSchemas([...newSchemas]);
    } else {
      setSchemas([newSchemas[newSchemas.length - 1]]); // only keep the last one
    }
  };

  const onClickRemoveItem = (schemaId: string) => {
    if (schemaId) {
      const newSchemas = selectedSchemas.filter(
        (schema) => schema.id !== schemaId,
      );
      setSchemas(newSchemas);
    }
  };
  const renderMultiValueLabel = (
    props: MultiValueGenericProps<IBasicSchema>,
  ) => {
    return (
      <BasicEntity onClear={() => onClickRemoveItem(props.data.id)}>
        {props.data.name}
      </BasicEntity>
    );
  };

  const onChangeSingleSchema = (selectedSchemaId: string) => {
    if (!selectedSchemaId) {
      return;
    } else {
      const selectedSchema = schemaOptions.find(
        (schemaOption) => schemaOption.id === selectedSchemaId,
      );
      assertNotNil(selectedSchema);
      setSchemas([selectedSchema]);
    }
  };

  if (!isMulti) {
    return (
      <BuilderSelectMenu
        options={schemaOptions.map((schemaOption) => ({
          text: schemaOption.name,
          value: schemaOption.id,
        }))}
        value={selectedSchemas[0]?.id}
        onChange={(selectedSchemaId) => onChangeSingleSchema(selectedSchemaId)}
        disabled={schemaOptions.length === 1}
      />
    );
  }

  return (
    <ReactSelectMenu
      menuPosition={'fixed'}
      options={schemaOptions}
      value={selectedSchemas}
      onChange={onChange}
      isMulti={true}
      isSearchable={false}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.id}
      components={{
        MultiValueLabel: renderMultiValueLabel,
        DropdownIndicator: (props) =>
          BasicDropdownIndicator({ props, color: theme.colors.secondary900 }),
      }}
    />
  );
};

export default PermissionSchemasSelector;
